// src/features/atividade/atividadeContrato.js
//
// A parte PURA do salvamento: o que a atividade de GPS vira no formato do
// check-in de treino, e como ela se chama quando ninguém digita um título.
//
// ─── POR QUE ESTÁ SEPARADO DE atividadeService.js ───────────────────────────
//
// O serviço importa `firebase/firestore`, `firebase/auth` e o `firebaseConfig`.
// Carregá-lo num teste inicializaria o SDK do Firebase e exigiria as variáveis
// de ambiente de produção — para conferir uma multiplicação. Aqui não há
// import de rede nenhum, e o teste roda com `node --test`.
//
// É a mesma separação que `moedaPolitica` faz do `moedaCredito` no servidor: a
// decisão pura de um lado, o Firestore do outro.

import { calcPoints, estimarCalorias } from '../treinos/utils/pointsConfig.js';
import { modalidade } from './atividadeConfig.js';

/**
 * Título padrão — o que "Pular" na tela 2F deixa salvo.
 *
 * Período do dia + modalidade, no fuso de São Paulo. O mesmo par de campos que
 * a pessoa escreveria, e melhor do que "Atividade" repetido trinta vezes na
 * lista.
 *
 * @param {string} modalidadeId
 * @param {number} inicioMs
 * @returns {string}
 */
export function tituloPadrao(modalidadeId, inicioMs) {
  const mod = modalidade(modalidadeId);
  const hora = Number(
    new Intl.DateTimeFormat('pt-BR', {
      timeZone: 'America/Sao_Paulo',
      hour: 'numeric',
      hour12: false,
    }).format(new Date(inicioMs || Date.now()))
  );
  const periodo =
    hora < 6 ? 'de madrugada' : hora < 12 ? 'da manhã' : hora < 18 ? 'da tarde' : 'da noite';
  return `${mod.nome} ${periodo}`;
}

/** As três sugestões de título da tela 2F. */
export function sugestoesDeTitulo(modalidadeId, inicioMs, distanciaM) {
  const mod = modalidade(modalidadeId);
  const km = (Number(distanciaM) || 0) / 1000;
  return [
    tituloPadrao(modalidadeId, inicioMs),
    km >= 1
      ? `${km.toFixed(1).replace('.', ',')} km de ${mod.nome.toLowerCase()}`
      : `${mod.nome} curta`,
    'Só mais um dia',
  ];
}

/**
 * A atividade de GPS no formato que o `createTreinoCheckin` entende.
 *
 * É o contrato de `buildPredictedAtividade` (workoutXp.js) aplicado à
 * gravação: mesma forma de item, mesmos campos, MESMA função de pontos. É o
 * que garante que o XP de uma corrida gravada seja o XP de uma corrida
 * registrada com os mesmos números — e a razão de não haver fórmula de pontos
 * nenhuma neste arquivo.
 *
 * `duration_min` é o tempo EM MOVIMENTO, já sem as pausas: é esse campo que o
 * gate de 40 min do servidor lê, e mandar o decorrido daria XP a quem ficou
 * meia hora parado no banco da praça.
 *
 * @param {object} args `{ modalidadeId, resumoAtividade, pesoKg }`
 * @returns {Array<object>} `atividades[]`
 */
export function montarAtividadeDoCheckin({ modalidadeId, resumoAtividade, pesoKg }) {
  const mod = modalidade(modalidadeId);
  const durationMin = Math.round((resumoAtividade?.duracaoS || 0) / 60);
  const distanceKm = (resumoAtividade?.distanciaM || 0) / 1000;
  const { kcal } = estimarCalorias({ key: mod.activityKey, durationMin, pesoKg });
  const steps = resumoAtividade?.passos || 0;

  return [
    {
      activity_key: mod.activityKey,
      activity_label: mod.nome,
      duration_min: durationMin,
      duration_sec: resumoAtividade?.duracaoS || 0,
      distance_km: Number(distanceKm.toFixed(3)),
      calories: kcal,
      steps,
      points: calcPoints({
        key: mod.activityKey,
        durationMin,
        distanceKm,
        calories: kcal,
        steps,
      }),
      // Métricas estruturadas para o card de compartilhamento e para o detalhe.
      // `workoutMetrics.js` lê campo, nunca parseia a descrição.
      distancia_m: resumoAtividade?.distanciaM || 0,
      pace_medio_s: Math.round(resumoAtividade?.paceMedioS || 0) || null,
      elevacao_pos: resumoAtividade?.elevacaoPos || 0,
      gps: true,
    },
  ];
}

/**
 * O payload do `createTreinoCheckin`.
 *
 * A CHAVE DE IDEMPOTÊNCIA é o id da atividade, gerado no aparelho — o MESMO id
 * que vira o documento em ATIVIDADES e o `eventKey` do crédito de moeda. Um id
 * só para os três. Se eles divergirem, um retry de rede no meio da celebração
 * cria um segundo check-in: XP em dobro, ofensiva contada a mais, e ninguém vê.
 *
 * @param {object} args `{ bruto, titulo, descricao, atividades }`
 * @returns {object} payload
 */
export function montarPayloadCheckin({
  bruto,
  titulo,
  descricao = '',
  atividades,
  atividadeGpsId = null,
}) {
  return {
    idempotency_key: bruto.id,
    // Aponta para o documento em ATIVIDADES que já foi gravado e conferido.
    // É por ele que o servidor troca o gate de 40 min/foto pelo gate de GPS —
    // e é ELE que o servidor lê, não a flag `gps` de dentro de `atividades[]`,
    // que é palavra do cliente.
    atividade_gps_id: atividadeGpsId,
    title: titulo,
    description: String(descricao || '').trim(),
    location: '',
    checkinAtISO: new Date(bruto.inicio || Date.now()).toISOString(),
    photo_url: '',
    photo_path: '',
    atividades,
    // Não é sessão de treino ao vivo: o bônus de "live" é da tela de séries,
    // não daqui. Pedi-lo aqui seria pagar duas vezes pelo mesmo mérito.
    live_workout: false,
  };
}

/**
 * O que o servidor creditou por esta atividade, somando as duas callables.
 *
 * `registrarAtividadeGps` paga o bônus por quilômetro; `createTreinoCheckin`
 * paga o XP e a moeda base. Quem quiser o total tem de somar os dois — e a
 * soma existe aqui, e não nos dois lugares que precisam dela (a tela que salva
 * e o aviso de crédito atrasado), porque duas cópias divergiriam em silêncio e
 * o sintoma seria um número diferente em cada tela para a mesma corrida.
 *
 * @param {object} envio `{ registro, checkin }` como o servidor devolveu
 * @returns {{xp:number, moedas:number}} zero quando não creditou
 */
export function recompensasDoEnvio(envio) {
  const registro = envio?.registro || null;
  const checkin = envio?.checkin || null;

  const moedaCheckin = checkin?.moeda?.creditou
    ? Number(checkin.moeda.valorFinal) || 0
    : 0;

  return {
    xp: Number(checkin?.xp_earned) || 0,
    moedas: (Number(registro?.moedas) || 0) + moedaCheckin,
  };
}

export default {
  tituloPadrao,
  sugestoesDeTitulo,
  montarAtividadeDoCheckin,
  montarPayloadCheckin,
  recompensasDoEnvio,
};
